import React, { useEffect, useState } from "react";
import {
  Col,
  FormSelect,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Row,
} from "shards-react";
import { getHistoricGraphData } from "../../backend/BinAlgData";
import { parseDate } from "../../backend/common";
import SmallStats from "./SmallStats2";
import WeeklyDatePicker from "./WeeklyDatePicker";
import { Animals, VACUNO_MACHO_KEY } from "../../utils/constants";

const moment = require("moment");

export const BovinePrice = () => {
  const [fromDate, setFromDate] = useState(
    moment().startOf("isoWeek").subtract(9, "week")
  );
  const [toDate, setToDate] = useState(moment().startOf("isoWeek").add(1, "w"));

  const [animalType, setAnimalType] = useState({
    key: VACUNO_MACHO_KEY,
    sex: Animals.bovineMale.sex,
    weight: Animals.bovineMale.weight[2],
    fatType: Animals.bovineMale.fatType[2],
    type: Animals.bovineMale.type,
  });

  const [prices, setPrice] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [dates, setDates] = useState([]);

  const getData = () => {
    // Set default values
    setPrice([]);
    setPredictions([]);
    // Get data from backend
    getHistoricGraphData(
      {
        sex: animalType.sex,
        weight: animalType.weight,
        fatType: animalType.fatType,
        type: animalType.type,
        from: fromDate.clone().format("DD-MM-YYYY"),
        to: toDate.clone().format("DD-MM-YYYY"),
      },
      (result) => {
        const priceResult = result.binefar;
        const predictionResult = result.predicted;
        // Get date range from fromDate to toDate
        let datesR = [];
        let date = fromDate.clone();
        while (date.isSameOrBefore(toDate)) {
          // Format: 2023-10-02T00:00:00.000Z
          datesR.push(date.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"));
          date.add(1, "week");
        }
        // Fit the data to the date range
        const priceR = datesR.map((date) => {
          return (
            priceResult.find((val) => val.date.standard === date)?.price ??
            undefined
          );
        });
        const predictionR = datesR.map((date) => {
          return (
            predictionResult.find((val) => val.date.standard === date)?.price ??
            undefined
          );
        });
        // Set the state
        setDates(datesR);
        setPredictions(predictionR);
        setPrice(priceR);
      }
    );
  };

  // Helper function to calculate min/max with conditional limits
  const getChartLimits = () => {
    const allData = [
      ...prices.filter((val) => val !== undefined && !isNaN(val)),

      ...predictions.filter((val) => val !== undefined && !isNaN(val)),
    ];

    const validData = allData.filter((val) => val !== undefined && !isNaN(val));
    if (validData.length === 0) return { min: undefined, max: undefined };

    const minValue = Math.min(...validData);
    const maxValue = Math.max(...validData);
    const range = maxValue - minValue;

    // Only set limits if the range is less than 0.4
    return {
      min: minValue - 0.05,
      max: maxValue + 0.05,
    };
  };

  // Calculate limits for the first chart (all price data combined)

  const firstChartLimits = getChartLimits();

  useEffect(() => {
    getData();
  }, [animalType, fromDate, toDate]);

  return (
    <>
      <Row>
        <Col md="12" lg="6">
          <label>Fecha</label>
          <InputGroup className="flex-wrap">
            <WeeklyDatePicker date={fromDate} changeDate={setFromDate} />
            <WeeklyDatePicker date={toDate} changeDate={setToDate} />
            <InputGroupAddon type="append">
              <InputGroupText>
                <i className="material-icons">&#xE916;</i>
              </InputGroupText>
            </InputGroupAddon>
          </InputGroup>
        </Col>
        <Col md="12" lg="2">
          <label>Animal</label>
          <InputGroup>
            <FormSelect
              className="mb-2"
              onChange={(event) => {
                const animal = JSON.parse(event.target.value);
                setAnimalType({
                  key: event.target.options[
                    event.target.selectedIndex
                  ].getAttribute("data-key"),
                  sex: animal.sex,
                  weight: Object.values(animal.weight)[0],
                  fatType: Object.values(animal.fatType)[0],
                  type: animal.type,
                });
              }}
            >
              {Object.entries(Animals).map((val) => (
                <option
                  key={val[0]}
                  data-key={val[0]}
                  value={JSON.stringify(val[1])}
                >
                  {val[1].name}
                </option>
              ))}
            </FormSelect>
          </InputGroup>
        </Col>
        <Col md="12" lg="2">
          <label>Categoría de pesos</label>
          <InputGroup>
            <FormSelect
              className="mb-2"
              value={animalType.weight}
              onChange={(event) => {
                setAnimalType({
                  ...animalType,
                  weight: event.target.value,
                });
              }}
            >
              {Object.entries(Animals[animalType.key].weight).map((val) => (
                <option key={val[0]} value={val[1]}>
                  {val[1]}
                </option>
              ))}
            </FormSelect>
          </InputGroup>
        </Col>
        <Col md="12" lg="2">
          <label>Categoría de grasa</label>
          <InputGroup>
            <FormSelect
              value={animalType.fatType}
              onChange={(event) =>
                setAnimalType({ ...animalType, fatType: event.target.value })
              }
            >
              {Object.values(Animals[animalType.key].fatType).map((fatType) => (
                <option key={fatType} value={fatType}>
                  {fatType}
                </option>
              ))}
            </FormSelect>
          </InputGroup>
        </Col>
      </Row>
      <SmallStats
        id={`small-stats-0`}
        variation="2"
        chartData={{
          labels: dates.map((val) => parseDate(val)),
          datasets: [
            {
              label: "Real Binéfar",
              fill: "start",
              borderWidth: 1.5,
              backgroundColor: "rgba(255,180,0,0.4)",
              borderColor: "rgba(255,180,0)",
              data: prices,
              align: "start",
              anchor: "start",
              display: true,
            },
            {
              label: "Predicción",
              backgroundColor: "rgba(255,65,105,0.4)",
              borderColor: "rgba(255,65,105,1)",
              data: predictions,
              align: "end",
              anchor: "end",
              fill: "start",
              display: "auto",
            },
          ],
        }}
        max={firstChartLimits.max}
        min={firstChartLimits.min}
        label={`Predicción ${Animals[animalType.key].name} ${
          animalType.weight
        } ${animalType.fatType} =`}
      />
    </>
  );
};
export default BovinePrice;
